import type { SubscriptionBillingPayload } from '../types/subscriptionBilling';
import { fetchWithNonce } from '../utils/fetchWithNonce';
import { parseApiError } from '../utils/parseApiError';

type FetchSubscriptionBillingParams = {
  baseUrl: string;
  nonce: string;
};

export type FetchSubscriptionBillingResult =
  | { ok: true; data: SubscriptionBillingPayload }
  | { ok: false; status: number; message: string };

export const fetchSubscriptionBilling = async ({
  baseUrl,
  nonce,
}: FetchSubscriptionBillingParams): Promise<FetchSubscriptionBillingResult> => {
  const response = await fetchWithNonce(`${baseUrl}subscription-billing`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { 'X-WP-Nonce': nonce },
  });

  if (response.status === 401 || response.status === 403) {
    return { ok: false, status: response.status, message: '' };
  }

  if (!response.ok) {
    return { ok: false, status: response.status, message: await parseApiError(response) };
  }

  const data = (await response.json()) as SubscriptionBillingPayload;
  return { ok: true, data };
};

export type CancelAtPeriodEndResult =
  | { ok: true; data: { cancelAtPeriodEnd: boolean; currentPeriodEndUtc: string | null; accessState: string | null } }
  | { ok: false; status: number; message: string };

export const postCancelSubscriptionAtPeriodEnd = async ({
  baseUrl,
  nonce,
}: FetchSubscriptionBillingParams): Promise<CancelAtPeriodEndResult> => {
  const response = await fetchWithNonce(`${baseUrl}subscription-billing/cancel-at-period-end`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'X-WP-Nonce': nonce,
      'Content-Type': 'application/json',
    },
    body: '{}',
  });

  if (response.status === 401 || response.status === 403) {
    return { ok: false, status: response.status, message: '' };
  }

  if (!response.ok) {
    return { ok: false, status: response.status, message: await parseApiError(response) };
  }

  const data = (await response.json()) as {
    cancelAtPeriodEnd: boolean;
    currentPeriodEndUtc: string | null;
    accessState: string | null;
  };
  return { ok: true, data };
};

export type CancelPendingPlanChangeResult =
  | { ok: true; data: { pendingPlanChangeCleared: boolean } }
  | { ok: false; status: number; message: string };

export const postCancelPendingPlanChange = async ({
  baseUrl,
  nonce,
}: FetchSubscriptionBillingParams): Promise<CancelPendingPlanChangeResult> => {
  const response = await fetchWithNonce(`${baseUrl}subscription-billing/cancel-pending-plan-change`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'X-WP-Nonce': nonce,
      'Content-Type': 'application/json',
    },
    body: '{}',
  });

  if (response.status === 401 || response.status === 403) {
    return { ok: false, status: response.status, message: '' };
  }

  if (!response.ok) {
    return { ok: false, status: response.status, message: await parseApiError(response) };
  }

  const data = (await response.json()) as { pendingPlanChangeCleared: boolean };
  return { ok: true, data };
};
